import { Message } from '@scale/shared/types/chat-completion';
import cn from 'classnames';
import { ChatMessageDisplayType, MessageRole } from '@scale/shared/types/chat';
import {
  useChatLayoutStore,
  ChatLayout,
} from '../../../ThreadedInteraction/hooks/useChatLayoutStore';
import { usePinStore } from '../../../ThreadedInteraction/hooks/usePinStore';
import UtilitiesWrapper from '../../../common/UtilitiesWrapper';
import { ChatMarkdown } from 'react-scale/components/scalers/tasks/ThreadedInteraction/components/ChatMarkdown';
import { useConfigureChatMessageDisplay } from 'react-scale/components/scalers/tasks/ThreadedInteraction/hooks/useConfigureChatMessageDisplay';
import { twMerge } from 'tailwind-merge';
import { StepCard } from '../../../ThreadedInteraction/step/StepCard';
import { ChatMessageDisplayConfigButton } from '../../../ThreadedInteraction/step/ChatMessageDisplayConfig';
import { useUser } from 'react-scale/components/ui/UserContext';

interface Props {
  multiChatIndex: number;
  className?: string;
  message: Message;
  readOnly?: boolean;
  disableCopying?: boolean;
  enablePinning?: boolean;
  // turnIndex?: number; // TODO
  expandable?: boolean;
  expanded?: boolean;
  enableRTLText?: boolean;
  responseIndex: number; // step.responseIndex
  // TODO: handle these props
  // referenceTexts={responses.at(step.responseIndex)?.context?.referenceTexts}
  // enableAttachments={config.useAttachments}
  // attachments={responses.at(step.responseIndex)?.context?.attachments}
  // enableRTLText={enableRTLTextPromptInput}
}

// eslint-disable-next-line complexity
export function PinnedPrompt({
  multiChatIndex,
  className,
  message,
  disableCopying,
  enablePinning,
  // turnIndex,
  expandable = false,
  expanded = false,
  enableRTLText,
  responseIndex,
}: Props) {
  const user = useUser();
  const isCorpUser: boolean = !!user?.isCorporateUser;

  const isDrawerOpen = useChatLayoutStore(state => state.isDrawerOpen);
  const setIsDrawerOpen = useChatLayoutStore(state => state.setIsDrawerOpen);
  const layout = useChatLayoutStore(state => state.layout);

  const {
    chatMessageDisplayConfig,
    useMarkdownDisplay,
    updateMarkdownConfig,
    toggleChatMessageDisplayType,
  } = useConfigureChatMessageDisplay();

  const isPinned = usePinStore(state => state.isPinnedMessage(responseIndex, multiChatIndex));
  const isPinnable = usePinStore(state => enablePinning && state.isPinnable(responseIndex));
  const addPinnedMessage = usePinStore(state => state.addPinnedMessage);
  const removePinnedMessage = usePinStore(state => state.removePinnedMessage);
  const showPinAction = isPinnable && layout === ChatLayout.Inline;

  const enablePromptCopying = (!disableCopying && !expanded) || isCorpUser;

  return (
    <>
      <StepCard className={twMerge(cn('focus:ring-0', className))}>
        <StepCard.Header>
          <StepCard.HeaderLeft>
            {message.role === MessageRole.User ? (
              <StepCard.Icon icon="user" />
            ) : (
              <StepCard.Image src="https://static.remotasks.com/uploads/602b25a6e0984c00343d3b26/Frame.png" />
            )}
            <StepCard.HeaderColumn>
              <StepCard.Title className="inline">
                Chat #{multiChatIndex + 1}
                {message.role === MessageRole.User ? ' - Prompt' : ' - Response'}
              </StepCard.Title>
            </StepCard.HeaderColumn>
          </StepCard.HeaderLeft>

          <StepCard.HeaderRight>
            {enablePromptCopying && (
              <StepCard.Button
                tooltip="Copy this chat message"
                icon="copy"
                onClick={e => {
                  e.stopPropagation();
                  navigator.clipboard.writeText(message.content);
                }}
              />
            )}
            <StepCard.Button
              icon={isPinned ? 'slash' : 'thumbtack'}
              iconProps={{
                mask: isPinned ? 'thumbtack' : undefined,
                transform: isPinned ? 'down-1' : undefined,
              }}
              disabled={!showPinAction}
              tooltip={
                !showPinAction
                  ? 'Open the pinned item drawer to pin this step'
                  : isPinned
                  ? 'Unpin this prompt'
                  : 'Pin this prompt'
              }
              onClick={() => {
                if (!isPinnable) return;
                if (!isDrawerOpen) setIsDrawerOpen(true);
                if (isPinned) {
                  removePinnedMessage(responseIndex, multiChatIndex);
                  return;
                }
                addPinnedMessage(responseIndex, message, multiChatIndex);
              }}
              enableDisabledTooltip
            />
            {!expanded && (
              <ChatMessageDisplayConfigButton
                chatMessageDisplayConfig={chatMessageDisplayConfig}
                useMarkdownDisplay={useMarkdownDisplay}
                updateMarkdownConfig={updateMarkdownConfig}
                toggleChatMessageDisplayType={toggleChatMessageDisplayType}
              />
            )}
            {expandable && expanded && (
              <StepCard.Button tooltip="Hide step history" icon="chevron-up" />
            )}
            {expandable && !expanded && (
              <StepCard.Button tooltip="Expand and show step history" icon="chevron-down" />
            )}
          </StepCard.HeaderRight>
        </StepCard.Header>
        <StepCard.Body className={cn('flex flex-row w-full px-4 py-3 items-center')}>
          <div className="flex ml-4 flex-col flex-1 w-full min-w-0">
            <div className="flex-1 w-full">
              <div
                className={cn('flex flex-col gap-1 w-full', {
                  'select-none': disableCopying,
                })}
              >
                {chatMessageDisplayConfig.displayType === ChatMessageDisplayType.Markdown ? (
                  <UtilitiesWrapper enableRTL={enableRTLText}>
                    <div className="w-full">
                      <ChatMarkdown
                        markdown={message.content}
                        enableMath={chatMessageDisplayConfig.markdownConfig.enableMathPlugin}
                        enableSingleDollarTextMath={
                          chatMessageDisplayConfig.markdownConfig.enableSingleDollarTextMath
                        }
                        // TODO: enable the following props
                        // enableHtml={taskContext.htmlMarkdownConfig?.enablePromptPreviews}
                        // enableSoftBreaks={taskContext.generalMarkdownConfig?.enableSoftLineBreaks}
                        // enableExecutableCodeBlocks={
                        //   taskContext.codingConfig?.enableExecutableCodeMarkdown ??
                        //   taskContext.generalMarkdownConfig?.enableExecutableCodeMarkdown
                        // }
                      />
                    </div>
                  </UtilitiesWrapper>
                ) : (
                  <div className="text-neutral-900 text-sm whitespace-pre-wrap">
                    {message.content}
                  </div>
                )}
              </div>
            </div>
          </div>
        </StepCard.Body>
      </StepCard>
    </>
  );
}
